import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, ClockAlert, CloudOff, Download, Package, ShoppingCart } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { canEdit } from '@/lib/permissions';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatNumber } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { StatCard } from '@/components/ui/stat-card';
import { notify } from '@/components/ui/notify';
import {
  ChipSelect, LinkButton, PhoneKpis, SearchField, Segmented, SfCard, plural, useIsPhone,
} from '@/components/supply-finance/SfUi';
import {
  FORECAST_EXPORT_COLUMNS, HORIZONS, dataGaps, filterLines, filterSuggestions, forecastKpis, horizonCounts, lpoPrefillFor,
  normalizeLines, withSiteDeliveries,
} from '@/components/supply-finance/forecastModel';
import { DataGapsChip, ExpectedDeliveriesCard } from '@/components/supply-finance/ForecastTables';
import { OrderNowCard, draftCover } from '@/components/supply-finance/OrderNowCard';
import { DeliveryTimeline } from '@/components/supply-finance/DeliveryTimeline';
import { LpoFormPanel } from '@/components/supply-finance/LpoFormPanel';
import { DemandOutlookCard } from '@/components/supply-finance/DemandOutlookCard';
import { ChaseSupplierPanel } from '@/components/supply-finance/ChaseSupplierPanel';
import { MaterialTracePanel } from '@/components/supply-finance/MaterialTracePanel';
import { datedFilename, downloadCsv } from '@/components/supply-finance/csv';

const DEMAND_WEEKS = 8;

function ForecastSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {['Late deliveries', 'Due next 30 days', 'Parts to order', 'Still to deliver'].map((label) => <StatCard key={label} label={label} loading />)}
      </div>
      <div className="ui-skeleton-shimmer h-[320px] rounded-card" />
    </div>
  );
}

export default function ForecastPage() {
  const isPhone = useIsPhone();
  const { user } = useAuth();
  // "Create LPO" needs Edit on Procurement (Admin › Access; the API checks it too); Viewers are read-only.
  const canCreateLpo = canEdit(user, 'procurement');
  const [selectedParts, setSelectedParts] = useState(() => new Set());
  const [lpoPanel, setLpoPanel] = useState({ open: false, prefill: null });
  // Parts ordered from this page in this visit → the new LPO's number ("On LPO-… · just now").
  const [orderedParts, setOrderedParts] = useState(() => new Map());
  // Delivered per LPO and part, into stock vs direct to site (null = not loaded / not needed).
  const [siteByLpo, setSiteByLpo] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState([]);

  const [horizon, setHorizon] = useState('all');
  const [search, setSearch] = useState('');
  const [projectId, setProjectId] = useState(null);
  const [category, setCategory] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [gapsOnly, setGapsOnly] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const orderNowRef = useRef(null);
  const demandRef = useRef(null);
  const requestRef = useRef(0);
  // 23 #8 demand outlook (GET /forecast/demand), 23 #9 chase supplier, 22 #10 "Where is it?"
  const [outlook, setOutlook] = useState({ data: null, loading: true, error: '' });
  const outlookRequestRef = useRef(0);
  const [chase, setChase] = useState(null);
  const [trace, setTrace] = useState(null);

  const load = useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const params = { months: 3 };
      if (projectId) params.project_id = projectId;
      if (category) params.category = category;
      const res = await api.get('/forecast', { params });
      if (requestRef.current !== requestId) return;
      setData(res.data);
      if (Array.isArray(res.data?.category_options)) setCategoryOptions(res.data.category_options);
    } catch (err) {
      if (requestRef.current !== requestId) return;
      setError(getErrorMessage(err, 'Could not load the forecast'));
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, [projectId, category]);

  useEffect(() => { load(); }, [load]);

  const loadOutlook = useCallback(async () => {
    const requestId = outlookRequestRef.current + 1;
    outlookRequestRef.current = requestId;
    setOutlook((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const params = { weeks: DEMAND_WEEKS };
      if (projectId) params.project_id = projectId;
      if (category) params.category = category;
      const res = await api.get('/forecast/demand', { params });
      if (outlookRequestRef.current === requestId) setOutlook({ data: res.data, loading: false, error: '' });
    } catch (err) {
      if (outlookRequestRef.current === requestId) setOutlook({ data: null, loading: false, error: getErrorMessage(err, 'Could not load the demand outlook') });
    }
  }, [projectId, category]);
  useEffect(() => { loadOutlook(); }, [loadOutlook]);
  const runningOut = outlook.data?.summary?.running_out || 0;
  const demandToOrder = outlook.data?.summary?.to_order;
  const scrollToDemand = () => demandRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  useEffect(() => {
    api.get('/projects').then((res) => setProjects(Array.isArray(res.data) ? res.data : [])).catch(() => setProjects([]));
  }, []);

  const allLines = useMemo(() => normalizeLines(data?.expected_deliveries), [data]);
  const rawSuggestions = useMemo(() => (Array.isArray(data?.order_suggestions) ? data.order_suggestions : []), [data]);
  // "Why short?" needs to know which delivered pieces went direct to site (never in stock). Only asked for the
  // LPOs behind parts whose delivered quantity is above what is in stock.
  const siteLpoIds = useMemo(() => [...new Set(rawSuggestions
    .filter((s) => (s.delivered_to_site_on_lpos === undefined || s.delivered_to_site_on_lpos === null)
      && Number(s.delivered_on_lpos) > Number(s.stock_on_hand ?? s.current_available ?? 0))
    .flatMap((s) => (Array.isArray(s.demand_lines) ? s.demand_lines : []).filter((line) => Number(line.delivered_qty) > 0).map((line) => line.lpo_id))
    .filter(Boolean))].sort(), [rawSuggestions]);
  const siteLpoKey = siteLpoIds.join('|');
  useEffect(() => {
    if (!siteLpoKey) { setSiteByLpo(null); return undefined; }
    let alive = true;
    // Repeated lpo_id parameters, at most 100 per request so the URL stays short behind any proxy.
    const ids = siteLpoKey.split('|');
    const chunks = [];
    for (let start = 0; start < ids.length; start += 100) chunks.push(ids.slice(start, start + 100));
    Promise.all(chunks.map((chunk) => api.get(`/lpos/deliveries-by-destination?${chunk.map((id) => `lpo_id=${encodeURIComponent(id)}`).join('&')}`)))
      .then((responses) => { if (alive) setSiteByLpo(Object.assign({}, ...responses.map((res) => res.data?.lpos || {}))); })
      .catch(() => { if (alive) setSiteByLpo(null); });
    return () => { alive = false; };
  }, [siteLpoKey]);
  const suggestionsAll = useMemo(() => withSiteDeliveries(rawSuggestions, siteByLpo), [rawSuggestions, siteByLpo]);

  const supplierOptions = useMemo(() => {
    const options = new Map();
    allLines.forEach((line) => {
      const entry = options.get(line.supplierKey) || { value: line.supplierKey, label: line.supplier || 'Supplier missing', count: 0, muted: !line.supplier };
      entry.count += 1;
      options.set(line.supplierKey, entry);
    });
    return [...options.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [allLines]);
  const supplierLabel = supplier ? (supplierOptions.find((option) => option.value === supplier)?.label || '') : '';

  // Supplier is a page filter (the API filters project and category): it scopes the KPIs, the header alerts,
  // the data gaps, Order now and the monthly chart. The horizon and search only narrow the table (search
  // also narrows Order now).
  const supplierLines = useMemo(() => filterLines(allLines, { supplier }), [allLines, supplier]);
  const supplierSuggestions = useMemo(() => filterSuggestions(suggestionsAll, {
    supplier,
    supplierLabel,
    supplierParts: supplier ? new Set(supplierLines.map((line) => line.part_number)) : null,
  }), [suggestionsAll, supplier, supplierLabel, supplierLines]);
  const gaps = useMemo(() => dataGaps(supplierLines, supplierSuggestions), [supplierLines, supplierSuggestions]);
  const gapParts = useMemo(() => (gapsOnly ? new Set(gaps.map((gap) => gap.partNumber)) : null), [gapsOnly, gaps]);

  const scopedLines = useMemo(() => filterLines(supplierLines, { partNumbers: gapParts }), [supplierLines, gapParts]);
  const counts = useMemo(() => horizonCounts(scopedLines), [scopedLines]);
  const visibleLines = useMemo(
    () => filterLines(scopedLines, { horizon, search }),
    [scopedLines, horizon, search]
  );
  const suggestions = useMemo(
    () => filterSuggestions(supplierSuggestions, { search, partNumbers: gapParts }),
    [supplierSuggestions, search, gapParts]
  );
  const kpis = useMemo(() => forecastKpis(scopedLines, suggestions), [scopedLines, suggestions]);
  const projectOptions = useMemo(
    () => projects.map((project) => ({ value: project.id, label: project.name })).sort((a, b) => a.label.localeCompare(b.label)),
    [projects]
  );

  const filtersSet = Boolean(projectId || category || supplier || gapsOnly || search.trim() || horizon !== 'all');
  const clearFilters = () => {
    setHorizon('all'); setSearch(''); setProjectId(null); setCategory(null); setSupplier(null); setGapsOnly(false);
  };
  const scrollToOrderNow = () => orderNowRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

  // "Create LPO" (one part), "Create LPO for N parts" (the ticked ones) and the header's "Order N short parts"
  // all open New LPO filled in with those parts, their short quantity and the supplier to order from.
  const orderParts = useCallback((parts) => {
    if (!parts.length) return;
    setLpoPanel({ open: true, prefill: lpoPrefillFor(parts) });
  }, []);
  const pickedSuggestions = suggestions.filter((s) => selectedParts.has(s.part_number));
  // A part whose whole shortage is already on a draft LPO (22 #9) is sent from Procurement, not ordered again.
  const orderTargets = pickedSuggestions.length ? pickedSuggestions : suggestions.filter((s) => !draftCover(s).covers);
  const lpoSaved = (lpo) => {
    setSelectedParts(new Set());
    const number = String(lpo?.lpo_number || '').trim();
    const parts = (Array.isArray(lpo?.line_items) ? lpo.line_items : []).map((line) => line.part_number).filter(Boolean);
    if (number && parts.length) {
      setOrderedParts((prev) => {
        const next = new Map(prev);
        parts.forEach((part) => next.set(part, number));
        return next;
      });
    }
    load();
    loadOutlook();
  };

  const exportLines = () => {
    downloadCsv(datedFilename('forecast-expected-deliveries'), visibleLines, FORECAST_EXPORT_COLUMNS);
    notify.success(`Exported ${plural(visibleLines.length, 'line')}`);
  };

  const orderByLabel = kpis.orderBy ? formatDate(kpis.orderBy, { year: 'never' }) : '';
  const allStockGaps = kpis.partsToOrder > 0 && kpis.stockGapParts === kpis.partsToOrder;
  const header = (
    <PageHeader
      title="Forecast"
      count={data ? formatNumber(allLines.length) : undefined}
      subtitle="Incoming LPO deliveries and parts to order"
      alerts={[
        kpis.lateLines > 0 && { key: 'late', label: `${plural(kpis.lateLines, 'line')} late`, tone: 'red', onClick: () => setHorizon('late') },
        kpis.partsToOrder > 0 && {
          key: 'order',
          // When every short part is pieces already delivered that are not in stock, buying is not the next step.
          label: allStockGaps
            ? `${plural(kpis.partsToOrder, 'part')} short · check stock`
            : `${plural(kpis.partsToOrder, 'part')} to order${orderByLabel ? ` by ${orderByLabel}` : ''}`,
          tone: 'amber',
          onClick: scrollToOrderNow,
        },
        runningOut > 0 && {
          key: 'runs-out',
          label: `${plural(runningOut, 'part')} ${runningOut === 1 ? 'runs' : 'run'} out within ${DEMAND_WEEKS} weeks${demandToOrder === 0 ? ' · all on order' : ''}`,
          // Red while something still has to be ordered; amber when every one is on an LPO or a draft already.
          tone: demandToOrder === 0 ? 'amber' : 'red',
          onClick: scrollToDemand,
        },
      ]}
      actions={(
        <>
          <Button variant="ghost" onClick={exportLines} disabled={!visibleLines.length} className="px-2.5 text-ink-2 sm:px-3.5" aria-label="Export" data-testid="forecast-export">
            <Download aria-hidden="true" /><span className="hidden sm:inline">Export</span>
          </Button>
          {canCreateLpo && orderTargets.length ? (
            // Not the page's primary action when every short part is pieces delivered but not in stock:
            // ordering again would not clear them (the rows say "Check stock" first).
            <Button
              variant={allStockGaps ? 'outline' : 'default'}
              onClick={() => orderParts(orderTargets)}
              title={allStockGaps ? 'Every short part was delivered but is not in stock — check stock before ordering' : undefined}
              data-testid="forecast-order-short"
            >
              <ShoppingCart aria-hidden="true" />
              Order {plural(orderTargets.length, pickedSuggestions.length ? 'selected part' : 'short part')}
            </Button>
          ) : null}
        </>
      )}
    />
  );

  const filterRow = (
    <div className="flex flex-wrap items-center gap-2" data-testid="forecast-filters">
      <Segmented
        ariaLabel="Delivery horizon"
        value={horizon}
        onChange={setHorizon}
        testId="forecast-horizon"
        options={HORIZONS.map((option) => ({
          key: option.key,
          label: option.label,
          count: counts[option.key] ?? 0,
          dot: option.key === 'late' && counts.late ? 'red' : undefined,
          testId: `forecast-horizon-${option.key}`,
        }))}
      />
      <SearchField value={search} onChange={setSearch} placeholder="Search part or LPO" label="Search parts or LPOs" shortcut className="min-w-0 flex-1 basis-[180px] sm:w-[200px] sm:flex-none" testId="forecast-search" />
      <ChipSelect label="Project" value={projectId} options={projectOptions} onChange={setProjectId} testId="forecast-filter-project" emptyText="No projects" />
      <ChipSelect label="Supplier" value={supplier} options={supplierOptions} onChange={setSupplier} testId="forecast-filter-supplier" />
      <ChipSelect
        label="Category"
        value={category}
        options={categoryOptions.map((value) => ({ value, label: value }))}
        onChange={setCategory}
        testId="forecast-filter-category"
        emptyText="The catalog has no categories yet"
      />
      <DataGapsChip gaps={gaps} active={gapsOnly} onToggle={() => setGapsOnly((value) => !value)} />
      {filtersSet ? <LinkButton onClick={clearFilters} className="px-1">Clear filters</LinkButton> : null}
      <span className="ml-auto hidden text-[12.5px] text-ink-3 2xl:inline" data-testid="forecast-scope">
        {plural(visibleLines.length, 'line')} · {plural(new Set(visibleLines.map((line) => line.lpoKey)).size, 'LPO')}
      </span>
    </div>
  );

  const kpiStrip = isPhone ? (
    <PhoneKpis
      testId="forecast-kpis"
      items={[
        { key: 'late', value: formatNumber(kpis.lateLines), label: kpis.lateLines === 1 ? 'line late' : 'lines late', tone: kpis.lateLines ? 'red' : null, onClick: () => setHorizon('late') },
        { key: 'order', value: formatNumber(kpis.partsToOrder), label: 'parts to order', tone: kpis.partsToOrder ? 'amber' : null, onClick: scrollToOrderNow },
        { key: 'next30', value: formatNumber(kpis.next30Lines), label: 'due in 30 d', onClick: () => setHorizon('next30') },
      ]}
    />
  ) : (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="forecast-kpis">
      <StatCard
        label="Late deliveries"
        icon={ClockAlert}
        tone={kpis.lateLines ? 'red' : 'gray'}
        value={formatNumber(kpis.lateLines)}
        unit={kpis.lateLines === 1 ? 'line' : 'lines'}
        valueTone={kpis.lateLines ? 'red' : undefined}
        meta={kpis.lateLines ? `${plural(kpis.lateLpos, 'LPO')} · oldest ${formatNumber(kpis.oldestLateDays)} d` : 'Nothing past its expected date'}
        onClick={kpis.lateLines ? () => setHorizon('late') : undefined}
        data-testid="kpi-late-lines"
      />
      <StatCard
        label="Due next 30 days"
        icon={CalendarDays}
        tone="navy"
        value={formatNumber(kpis.next30Lines)}
        unit={kpis.next30Lines ? (kpis.next30Lines === 1 ? 'line' : 'lines') : undefined}
        meta={kpis.next30Lines ? `${formatNumber(kpis.next30Pcs)} pcs on ${plural(kpis.next30Lpos, 'LPO')}` : 'None scheduled — ask suppliers'}
        onClick={() => setHorizon('next30')}
        data-testid="kpi-next-30"
      />
      <StatCard
        label="Parts to order"
        icon={ShoppingCart}
        tone="amber"
        value={formatNumber(kpis.partsToOrder)}
        valueTone={kpis.partsToOrder ? 'amber' : undefined}
        meta={kpis.partsToOrder
          ? kpis.stockGapParts
            ? `${formatNumber(kpis.shortPcs)} pcs · ${allStockGaps ? 'all' : formatNumber(kpis.stockGapParts)} delivered but not in stock`
            : `${formatNumber(kpis.shortPcs)} pcs${kpis.orderBy ? ` · order by ${formatDate(kpis.orderBy)}` : ''}`
          : suggestionsAll.length ? `${formatNumber(suggestionsAll.length)} short outside these filters` : 'Nothing short'}
        onClick={kpis.partsToOrder ? scrollToOrderNow : undefined}
        data-testid="kpi-parts-to-order"
      />
      {kpis.pricesShown ? (
        <StatCard
          label="Still to deliver"
          icon={Package}
          tone="blue"
          prefix="AED"
          value={formatNumber(kpis.stillToDeliverValue, { decimals: 0 })}
          meta={`${formatNumber(kpis.stillToDeliverPcs)} pcs on ${plural(kpis.openLpos, 'LPO')}${kpis.unpricedLines ? ` · ${plural(kpis.unpricedLines, 'line')} without a price` : ''}`}
          data-testid="kpi-still-to-deliver"
        />
      ) : (
        <StatCard
          label="Still to deliver"
          icon={Package}
          tone="blue"
          value={formatNumber(kpis.stillToDeliverPcs)}
          unit="pcs"
          meta={`on ${plural(kpis.openLpos, 'LPO')} · values need Procurement access`}
          data-testid="kpi-still-to-deliver"
        />
      )}
    </div>
  );

  return (
    <div data-testid="forecast-page" className="space-y-4">
      {header}
      {error && !data ? (
        <SfCard>
          <EmptyState layout="inline" tone="red" icon={CloudOff} title="Couldn't load the forecast" text={error}
            action={<Button variant="outline" size="sm" onClick={load}>Try again</Button>} />
        </SfCard>
      ) : !data ? (
        <ForecastSkeleton />
      ) : (
        <>
          {filterRow}
          {kpiStrip}
          {/* What needs doing first: the parts to order, then when the open LPOs were due, then the lines. */}
          <OrderNowCard
            suggestions={suggestions}
            cardRef={orderNowRef}
            hiddenCount={suggestionsAll.length - suggestions.length}
            canCreateLpo={canCreateLpo}
            selected={selectedParts}
            onSelectedChange={setSelectedParts}
            onCreateLpo={orderParts}
            ordered={orderedParts}
            onTrace={(target) => setTrace(target)}
          />
          <div ref={demandRef} className="scroll-mt-24">
            <DemandOutlookCard
              outlook={outlook.data}
              loading={outlook.loading}
              error={outlook.error}
              onRetry={loadOutlook}
              search={search}
              onTrace={(target) => setTrace(target)}
            />
          </div>
          {scopedLines.length ? <DeliveryTimeline lines={scopedLines} /> : null}
          <div className={loading ? 'opacity-60 transition-opacity' : undefined} aria-busy={loading}>
            <ExpectedDeliveriesCard
              lines={visibleLines}
              filtered={filtersSet}
              onClearFilters={clearFilters}
              onChase={canCreateLpo ? (group) => setChase({ id: group.lpoId, number: group.lpoNumber, supplier: group.supplier }) : undefined}
            />
          </div>
        </>
      )}
      <ChaseSupplierPanel
        open={Boolean(chase)}
        onOpenChange={(open) => { if (!open) setChase(null); }}
        lpoId={chase?.id}
        number={chase?.number}
        supplier={chase?.supplier}
        onChanged={() => { load(); loadOutlook(); }}
      />
      <MaterialTracePanel
        open={Boolean(trace)}
        onOpenChange={(open) => { if (!open) setTrace(null); }}
        part={trace?.part}
        projectId={projectId || undefined}
        projectName={projectId ? projects.find((project) => project.id === projectId)?.name : undefined}
        description={trace?.description}
      />
      {canCreateLpo ? (
        <LpoFormPanel
          open={lpoPanel.open}
          onOpenChange={(open) => setLpoPanel((prev) => ({ ...prev, open }))}
          mode="create"
          prefill={lpoPanel.prefill}
          suppliers={allLines.map((line) => line.supplier).filter(Boolean)}
          onSaved={lpoSaved}
        />
      ) : null}
    </div>
  );
}
